import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import { useTeam } from 'app/frontend/base/config/context/team'
import OverviewTileType, {
  skeletonHeightByTileType,
} from 'app/frontend/types/custom/OverviewTileType'
import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'
import { useOverviewEditorState, useTile } from '../context'
import { cn } from '@/lib/utils'
import TileControls from './TileControls'
import { TILE_SECTION_MAP } from '../config'
import { useOverviewData } from 'app/frontend/base/config/queries'
import { Skeleton } from '@/components/ui/skeleton'
import { SidebarTilesTileContent } from './tiles/SidebarTilesTile'
import Card from 'app/frontend/base/components/Card'

export const tileTypeToKey = (type: OverviewTileType): string => {
  return type
    .replace(/^Tile::/, '')
    .replace(/([a-z])([A-Z])/g, '$1-$2')
    .toLowerCase()
}

interface TileWrapperProps {
  className?: string
  cta?: React.ReactNode
  inContainer?: boolean
}

export const useGetTileName = () => {
  const { t } = useTranslation()

  return (type: OverviewTileType): string => {
    const key = tileTypeToKey(type)
    return t(`overview.tiles.${key}.label`)
  }
}

const TileWrapper = ({
  inContainer = true,
  cta,
  className,
  children,
}: PropsWithChildren<TileWrapperProps>) => {
  const { tile } = useTile()
  const { team } = useTeam()
  const { isCustomizing } = useOverviewEditorState()
  const { isLoading } = useOverviewData(team.id)
  const section = TILE_SECTION_MAP[tile.type] || 'base'

  const Component = inContainer ? Card : 'div'

  return (
    <ProtectedContent
      section={section}
      requirement={tile.accessLevel}
      teamReference={team}
      renderUnauthorized={false}
    >
      {isLoading ? (
        <>
          {tile.type === 'Tile::SidebarTiles' ? (
            <SidebarTilesTileContent loading={isLoading} />
          ) : (
            <Skeleton
              className={cn('w-full', skeletonHeightByTileType[tile.type])}
            />
          )}
        </>
      ) : (
        <Component
          className={cn(
            'relative',
            isCustomizing ? 'min-h-20' : '',
            inContainer ? '' : 'w-full',
            className,
          )}
        >
          {!tile.readonly && isCustomizing && <TileControls />}

          <div
            className={cn(isCustomizing && 'blur-[2px] pointer-events-none')}
          >
            {tile.config?.title ? (
              <>
                <h5 className='mb-3 flex gap-2 justify-between'>
                  <span>{tile.config?.title}</span>
                  {cta && <div>{cta}</div>}
                </h5>
                <div>{children}</div>
              </>
            ) : (
              children
            )}
          </div>
        </Component>
      )}
    </ProtectedContent>
  )
}

export default TileWrapper
